/**
 * Copy text that is still being fetched when the click happens.
 *
 * The pending promise is handed to `ClipboardItem` instead of being awaited first: browsers only
 * allow a clipboard write that *starts* inside the click's transient user activation, and awaiting
 * a fetch throws that activation away. Doing so fails with "The request is not allowed by the user
 * agent or the platform in the current context", which reads like a denied permission but is really
 * an expired gesture.
 *
 * Chrome does not accept promise-valued clipboard items, so it falls through to `writeText` and
 * then to a hidden-textarea `execCommand` copy. Mirrors the HTML payload debugger's
 * `copyPayload` / `_writeToClipboard`.
 */
export async function writeTextToClipboard(
  textPromise: Promise<string>
): Promise<void> {
  if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textPromise.then(
            (text) => new Blob([text], { type: 'text/plain' })
          ),
        }),
      ]);
      return;
    } catch {
      // Promise-valued items are unsupported (or the text failed) — fall through.
    }
  }

  const text = await textPromise;
  if (navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Activation may already be gone; the textarea path is more permissive.
    }
  }
  if (!execCommandCopy(text)) {
    throw new Error('Failed to copy to clipboard');
  }
}

function execCommandCopy(text: string): boolean {
  const area = document.createElement('textarea');
  area.value = text;
  area.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
  document.body.appendChild(area);
  try {
    area.focus();
    area.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(area);
  }
}
